import { useState } from "react";
import { Bell, Calendar, FileText, Bot, Info, Trash2, Check, Filter, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from "@/hooks/use-notifications";
import { Notification, NotificationType } from "@/types/notification";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Obtiene el icono y color correspondiente al tipo de notificación.
 */
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case "DEADLINE":
      return { icon: <Calendar className="w-5 h-5" />, color: "text-orange-500 bg-orange-50" };
    case "STATUS_CHANGE":
      return { icon: <FileText className="w-5 h-5" />, color: "text-blue-500 bg-blue-50" };
    case "PROPOSAL_ASSIGNED":
      return { icon: <FileText className="w-5 h-5" />, color: "text-green-500 bg-green-50" };
    case "AGENT_COMPLETED":
      return { icon: <Bot className="w-5 h-5" />, color: "text-purple-500 bg-purple-50" };
    case "SYSTEM":
      return { icon: <Info className="w-5 h-5" />, color: "text-gray-500 bg-gray-50" };
    default:
      return { icon: <Bell className="w-5 h-5" />, color: "text-gray-500 bg-gray-50" };
  }
}

/**
 * Formatea la fecha de creación de la notificación.
 */
function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 1000 / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  // Si es hoy
  if (diffDay === 0) {
    if (diffMin < 1) return "ahora";
    if (diffMin < 60) return `hace ${diffMin}m`;
    return `hace ${diffHour}h`;
  }

  // Si es esta semana
  if (diffDay < 7) {
    return `hace ${diffDay}d`;
  }

  // Fecha completa
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Traduce el tipo de notificación a español.
 */
function translateNotificationType(type: NotificationType): string {
  switch (type) {
    case "DEADLINE":
      return "Fecha límite";
    case "STATUS_CHANGE":
      return "Cambio de estado";
    case "PROPOSAL_ASSIGNED":
      return "Propuesta asignada";
    case "AGENT_COMPLETED":
      return "Agente completado";
    case "SYSTEM":
      return "Sistema";
    default:
      return "Notificación";
  }
}

/**
 * Página completa de notificaciones.
 * Muestra todas las notificaciones con filtros y paginación.
 */
export default function NotificacionesPage() {
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<"all" | "unread" | NotificationType>("all");
  const pageSize = 20;
  const navigate = useNavigate();

  // Determinar parámetros de filtro
  const unreadOnly = filter === "unread";
  const typeFilter: NotificationType | null =
    filter !== "all" && filter !== "unread" ? filter : null;

  // Queries
  const { data, isLoading, isError } = useNotifications(page, pageSize, unreadOnly);
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  const notifications = data?.data || [];
  const totalPages = data?.total_pages || 1;

  /**
   * Filtra notificaciones por tipo si se ha seleccionado un filtro específico.
   */
  const filteredNotifications = typeFilter
    ? notifications.filter((n) => n.type === typeFilter)
    : notifications;

  /**
   * Maneja el click en una notificación.
   * Marca como leída y navega al link si existe.
   */
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsReadMutation.mutateAsync(notification.id);
    }

    if (notification.link) {
      navigate(notification.link);
    }
  };

  /**
   * Marca como leída sin navegar.
   */
  const handleMarkAsRead = async (
    e: React.MouseEvent,
    notificationId: string
  ) => {
    e.stopPropagation();
    await markAsReadMutation.mutateAsync(notificationId);
  };

  /**
   * Elimina una notificación.
   */
  const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
    e.stopPropagation();
    if (confirm("¿Eliminar esta notificación?")) {
      await deleteNotificationMutation.mutateAsync(notificationId);
    }
  };

  /**
   * Marca todas como leídas.
   */
  const handleMarkAllAsRead = async () => {
    await markAllAsReadMutation.mutateAsync();
  };

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notificaciones</h1>
          <p className="text-sm text-gray-500 mt-1">
            {data?.total || 0} notificaciones en total
          </p>
        </div>
        <Button
          onClick={handleMarkAllAsRead}
          disabled={markAllAsReadMutation.isPending}
          variant="outline"
          size="sm"
        >
          <Check className="w-4 h-4 mr-2" />
          Marcar todas como leídas
        </Button>
      </div>

      {/* Filtros */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filtrar:</span>
        </div>
        <Select
          value={filter}
          onValueChange={(value) => {
            setFilter(value as typeof filter);
            setPage(1); // Reset a página 1 al cambiar filtro
          }}
        >
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Todas" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="unread">No leídas</SelectItem>
            <SelectItem value="DEADLINE">Fechas límite</SelectItem>
            <SelectItem value="STATUS_CHANGE">Cambios de estado</SelectItem>
            <SelectItem value="PROPOSAL_ASSIGNED">Propuestas asignadas</SelectItem>
            <SelectItem value="AGENT_COMPLETED">Agentes completados</SelectItem>
            <SelectItem value="SYSTEM">Sistema</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de notificaciones */}
      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
          <p className="text-sm text-red-700">Error al cargar las notificaciones.</p>
        </div>
      ) : filteredNotifications.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No hay notificaciones
          </h3>
          <p className="text-sm text-gray-500">
            {filter === "unread"
              ? "No tienes notificaciones sin leer"
              : "Cuando recibas notificaciones, aparecerán aquí"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {filteredNotifications.map((notification) => {
            const { icon, color } = getNotificationIcon(notification.type);

            return (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                  !notification.is_read ? "bg-blue-50/30" : ""
                }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icono */}
                  <div
                    className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${color}`}
                  >
                    {icon}
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3
                        className={`text-sm ${
                          !notification.is_read
                            ? "font-semibold text-gray-900"
                            : "font-medium text-gray-700"
                        }`}
                      >
                        {notification.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {translateNotificationType(notification.type)}
                        </Badge>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-2">
                      {notification.message}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>{formatDate(notification.created_at)}</span>
                      {!notification.is_read && (
                        <Badge className="bg-blue-500 text-white text-xs px-2 py-0.5">
                          Nueva
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Acciones */}
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {!notification.is_read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleMarkAsRead(e, notification.id)}
                        title="Marcar como leída"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => handleDelete(e, notification.id)}
                      title="Eliminar"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Anterior
          </Button>
          <span className="text-sm text-gray-600">
            Página {page} de {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Siguiente
          </Button>
        </div>
      )}
    </div>
  );
}
